/**
 * <RubyGemGlow> -- THE LOADING GEM: the "nervous system" glow (Branding
 * "Pressure to Ruby", part 7). The gem glows forever while sparks fire slowly
 * along its facets. Same files and same rendering as the POS till
 * (AGENTIC_POS GemLoader / RubyGemDrawing) and platform-app (GemGlow).
 *
 * The SVGs are generated, never hand-edited:
 *   Branding/Ruby Till Branding-V2/08 SUNMI Devices/_build/build_till_svg.py
 * writes public/rubytill-gem-loader{,-bold}{,-still}.svg into this repo (bold
 * below 32px so the facets still read; still = no sparks). A static <img>: its
 * CSS keyframes run inside the image, so React never re-renders while it
 * plays. Ruby is the one colour on the light body and the black chrome alike.
 * Reduced motion: <picture> swaps to the still file (Chromium does not
 * reliably pass the media feature into <img> documents).
 *
 * Transparent, no backdrop, no caption. A brand glyph, not content imagery,
 * so not a ShimmerImage (`data-ruby-token="drawing"` marks the exemption).
 */
import React from 'react';

const glowSrc = (bold: boolean, still: boolean) =>
  `${import.meta.env.BASE_URL || '/'}rubytill-gem-loader${bold ? '-bold' : ''}${still ? '-still' : ''}.svg`;
/** The SVG is 1024 wide; the gem sits in its central 640. */
const GLOW_SCALE = 1024 / 640;

export interface RubyGemGlowProps {
  /** The gem's box in px (square). The faint halo may spill past it, never clipped. */
  size: number;
  /** Accessible name; omit for a decorative gem. */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const RubyGemGlow: React.FC<RubyGemGlowProps> = ({ size, title, className, style }) => {
  const img = size * GLOW_SCALE;
  const off = (size - img) / 2;
  const bold = size < 32;
  return (
    <span
      data-ruby-token="drawing"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
      style={{ position: 'relative', display: 'inline-block', flexShrink: 0, width: size, height: size, ...style }}
    >
      <picture>
        <source media="(prefers-reduced-motion: reduce)" srcSet={glowSrc(bold, true)} />
        <img
          src={glowSrc(bold, false)}
          alt=""
          data-ruby-token="drawing"
          draggable={false}
          width={img}
          height={img}
          style={{ position: 'absolute', left: off, top: off, width: img, height: img, maxWidth: 'none', pointerEvents: 'none' }}
        />
      </picture>
    </span>
  );
};

export default RubyGemGlow;
